import React, { memo, useCallback, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { FlexBox, useNotificationEvents } from '@agilepoint/wat-react-ux';
import { useTranslate } from '@agilepoint/wat-react';
import { AuthenticationForm } from 'modules/shared/forms';
import { IAuthenticationSettingsTextData, ISaveTextDataResponse } from 'modules/shared/queries';
import { useAuthenticationDefaultValues, useAuthenticationProviders } from 'modules/shared/hooks';
import { IAuthenticationFormFields } from 'modules/shared/types';
import { getAuthenticationSettingsJSON, AuthenticationSettingsResponse } from 'modules/shared/functions';
import { TabFooterButtons } from './tab-footer-buttons';

interface IAuthenticationProps {
    authMetadata: IAuthenticationSettingsTextData;
    mutationLoading: boolean;
    onSaveAuthenticationSettingsDetails: (args: AuthenticationSettingsResponse) => Promise<ISaveTextDataResponse>
}


const AuthenticationBase = React.memo((props: IAuthenticationProps) => {
    const { authMetadata, mutationLoading, onSaveAuthenticationSettingsDetails } = props;
    const providers = useAuthenticationProviders(authMetadata?.defaultConfig?.toLocaleLowerCase());
    const [optionsData, setOptionsData] = useState(providers);
    const { handleSubmit } = useFormContext<IAuthenticationFormFields>();
    const { showNotifications } = useNotificationEvents();
    const translate = useTranslate();
    const headerLabel = translate('modules.defaultAppSettings.authentication.authenticationHeaderLabel');

    const onMarkAsDefault = useCallback((key: React.Key) => {
        const clonedData = optionsData.slice();
        const modifiedOptionsData = clonedData.map((option) => {
            if (option.key === key) {
                option.value.markedAsDefault = true;
            }
            else option.value.markedAsDefault = false;
            return option;
        });
        setOptionsData(modifiedOptionsData);
    }, [optionsData]);

    const onSaveClick = useCallback((formData: IAuthenticationFormFields) => {
        onSaveAuthenticationSettingsDetails(getAuthenticationSettingsJSON(formData, optionsData))
            .then(() => showNotifications({ message: translate('modules.createNewApp.steps.authentication.savedAuthenticationDetailsSuccessful'), type: 'success' }))
            .catch(() => showNotifications({ message: translate('modules.createNewApp.steps.authentication.failedToSaveAuthDetails'), type: 'error' }));
    }, [onSaveAuthenticationSettingsDetails, optionsData, showNotifications, translate]);

    return (
        <>
            <FlexBox flexDirection='column' alignItems="center" height='100%' width="100%">
                <AuthenticationForm optionsData={optionsData} onMarkAsDefault={onMarkAsDefault} headerLabel={headerLabel} />
                <TabFooterButtons onSubmitClickHandler={handleSubmit(onSaveClick)} mutationLoading={mutationLoading} />
            </FlexBox>
        </>
    );
});

export const Authentication = memo((props: IAuthenticationProps) => {
    const { defaultConfig, ...rest } = props.authMetadata;
    const defaultValues = useAuthenticationDefaultValues(rest);

    const form = useForm<IAuthenticationFormFields>({
        mode: 'onChange',
        defaultValues
    });

    return (
        <FormProvider {...form}>
            <AuthenticationBase {...props} />
        </FormProvider>
    );
});
